import { CheckCircle } from '@mui/icons-material';
import {
  CircularProgress,
  Fade,
  Grow,
  Skeleton,
  styled,
  Table,
  TableCell,
  TableRow,
} from '@mui/material';
import Typography from '@mui/material/Typography';
import { BANK_RECONCILIATION } from 'adminConstants/routes';
import { CurrencyCode } from 'further-types/lp/vehicle';
import ColorIndicator from 'further-ui/components/ColorIndicator';
import { Column, Row } from 'further-ui/components/Layout';
import TypographyButton from 'further-ui/components/TypographyButton';
import { numberToCurrencyString, pluralize } from 'further-ui/utils';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

type StyledGrowProps = {
  staggerDelay?: number;
};

export const StyledGrow = styled(Grow, {
  shouldForwardProp: (prop) => prop !== 'staggerDelay',
})<StyledGrowProps>(({ staggerDelay = 0 }) => ({
  transformOrigin: 'center',
  transitionDelay: `${staggerDelay}ms`,
}));

const StyledTable = styled(Table)(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.divider}`,
  borderRight: `1px solid ${theme.palette.divider}`,
  borderLeft: `1px solid ${theme.palette.divider}`,
}));

const StyledTableCell = styled(TableCell)(() => ({
  width: '100%',
}));

const SkeletonRow = styled(Row)(() => ({
  width: '100%',
  justifyContent: 'space-between',
}));

const LeftBlock = styled(Row)(() => ({
  flex: 2,
  minWidth: 0,
}));

const LeftColumn = styled(Column)(() => ({
  flex: 1,
  minWidth: 0,
}));

const FullWidthSkeleton = styled(Skeleton)(() => ({
  width: '100%',
}));

interface FlexSkeletonProps {
  flex?: number;
}

const FlexSkeleton = styled(Skeleton, {
  shouldForwardProp: (prop) => prop !== 'flex',
})<FlexSkeletonProps>(({ flex }) => ({
  ...(flex && {
    flex,
    minWidth: 0,
  }),
}));

const FixedWidthColumn = styled(Column)(() => ({
  width: 21,
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const StatusOverlay = styled('div')(() => ({
  position: 'absolute',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const AnimatedCheck = styled(CheckCircle)(() => ({
  animation: 'pop 400ms ease-out',

  '@keyframes pop': {
    '0%': { transform: 'scale(0.8)', opacity: 0 },
    '60%': { transform: 'scale(1.15)' },
    '100%': { transform: 'scale(1)', opacity: 1 },
  },
}));

const CenteredContainer = styled('div')(() => ({
  height: '100%',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const MaxWidthColumn = styled(Column)(() => ({
  maxWidth: 600,
  width: '100%',
}));

type Props = {
  numberOfFees: number;
  loading: boolean;
  feeValue?: number;
  currencyLcy?: CurrencyCode;
};

const Processing: React.FC<Props> = ({
  numberOfFees,
  loading,
  feeValue = 0,
  currencyLcy,
}) => {
  const rows = [1, 2, 3];

  const [completedCount, setCompletedCount] = useState(0);

  useEffect(() => {
    if (!loading) {
      setCompletedCount(rows.length);
      return;
    }

    if (completedCount >= rows.length) return;

    const timer = setTimeout(() => {
      setCompletedCount((prev) => prev + 1);
    }, 800);

    return () => clearTimeout(timer);
  }, [loading, completedCount, rows.length]);

  return (
    <CenteredContainer>
      <MaxWidthColumn spacing="lg" centered fullWidth>
        <Typography variant="body2">
          {loading ? 'Approving and processing' : 'You approved'}{' '}
          <b>{numberOfFees}</b> {pluralize('fee', numberOfFees)}
          {loading ? '...' : '!'}
        </Typography>
        <StyledTable>
          {rows.map((row, index) => {
            const isComplete = index >= rows.length - completedCount;

            const staggerDelay = index * 120;

            return (
              <TableRow key={row}>
                <StyledTableCell align="center">
                  <SkeletonRow spacing="xxl" centered>
                    <LeftBlock spacing="sm" centered>
                      <ColorIndicator listIndex={row} />
                      <LeftColumn spacing="xxs">
                        <FullWidthSkeleton
                          animation={loading ? 'pulse' : false}
                          variant="rectangular"
                          height={25}
                        />
                        <FullWidthSkeleton
                          animation={loading ? 'pulse' : false}
                          variant="rectangular"
                          height={15}
                        />
                      </LeftColumn>
                    </LeftBlock>

                    <FlexSkeleton
                      variant="rectangular"
                      animation={loading ? 'pulse' : false}
                      height={25}
                      flex={2}
                    />

                    <Row spacing="md" flex={1.5}>
                      <FlexSkeleton
                        animation={loading ? 'pulse' : false}
                        variant="rectangular"
                        height={25}
                        flex={1}
                      />
                      <FixedWidthColumn centered>
                        <Fade in={!isComplete} timeout={300}>
                          <StatusOverlay>
                            <CircularProgress size={15} />
                          </StatusOverlay>
                        </Fade>

                        <StyledGrow
                          in={isComplete}
                          timeout={{
                            enter: 400,
                            exit: 200,
                          }}
                          staggerDelay={staggerDelay}
                        >
                          <StatusOverlay>
                            <AnimatedCheck fontSize="small" color="success" />
                          </StatusOverlay>
                        </StyledGrow>
                      </FixedWidthColumn>
                    </Row>
                  </SkeletonRow>
                </StyledTableCell>
              </TableRow>
            );
          })}
        </StyledTable>
        {loading ? null : (
          <Typography align="center" width="80%" variant="body2">
            {numberToCurrencyString(feeValue, { currency: currencyLcy })} is due
            to be paid to the fund manager and is available to be reconciled in{' '}
            <TypographyButton
              to={BANK_RECONCILIATION}
              component={Link}
              text="bank reconciliation"
            />{' '}
            when done.
          </Typography>
        )}
      </MaxWidthColumn>
    </CenteredContainer>
  );
};

export default Processing;
